import * as React from "react";

import { resolveCitationDocumentUrl } from "../../../services/gcpFileUrl";

interface EmbeddedIframePreviewProps {
  html?: string;
  src?: string;
  highlightUuid: string | null;
  citationColor: string;
  allowSameOrigin?: boolean;
}

export const EmbeddedIframePreview: React.FC<EmbeddedIframePreviewProps> = ({
  html,
  src,
  highlightUuid,
  citationColor,
  allowSameOrigin = true,
}) => {
  const iframeSrc = React.useMemo(
    () => src ?? extractIframeSrc(html ?? ""),
    [html, src]
  );
  const handleLoad = React.useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      if (!highlightUuid) return;
      const iframe = event.currentTarget;
      const postScrollMessage = () => {
        // Highlighted source HTML listens for this Finster Chat-compatible message.
        iframe.contentWindow?.postMessage(
          {
            type: "scroll-to-highlight",
            selector: highlightUuid,
            color: citationColor,
          },
          "*"
        );
      };
      postScrollMessage();
      window.setTimeout(postScrollMessage, 200);
      window.setTimeout(postScrollMessage, 700);
      window.setTimeout(postScrollMessage, 1400);
    },
    [citationColor, highlightUuid]
  );

  if (!iframeSrc) {
    return (
      <p className="text-[12px] text-muted-foreground">
        Source preview unavailable (iframe URL missing).
      </p>
    );
  }

  return (
    <section className="flex min-h-0 flex-1 overflow-hidden rounded-md border border-border bg-muted/20">
      <iframe
        title="Source preview"
        src={resolveCitationDocumentUrl(iframeSrc)}
        referrerPolicy="no-referrer"
        sandbox={`allow-scripts${allowSameOrigin ? " allow-same-origin" : ""} allow-popups`}
        onLoad={handleLoad}
        style={{ width: "100%", height: "100%", minHeight: 0, border: "0", display: "block" }}
      />
    </section>
  );
};

function extractIframeSrc(html: string): string | null {
  if (!html) return null;
  const match = html.match(/<iframe\b[^>]*\bsrc\s*=\s*("([^"]+)"|'([^']+)')/i);
  if (!match) return null;
  return match[2] ?? match[3] ?? null;
}
